/**
 * AuditLogsPage Component
 * Admin-only page for viewing audit logs with filters and CSV export
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Download, X, ChevronDown, ChevronRight, Copy, ScrollText } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Select } from '../components/ui/Select';
import { Button } from '../components/ui/Button';
import { Pagination } from '../components/ui/Pagination';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { usePaginationParams } from '../hooks/usePaginationParams';
import { auditLogsService, type AuditLogsQueryParams } from '../services/auditLogs.service';
import type { AuditLog, AuditLogActor } from '../types';
import { csvCell } from '../lib/csv';

/** Event type options grouped by category */
const EVENT_TYPE_OPTIONS = [
  { value: '', label: 'All Events' },
  { value: 'auth', label: 'Auth' },
  { value: 'member', label: 'Members' },
  { value: 'user', label: 'Users' },
  { value: 'password', label: 'Passwords' },
  { value: 'team', label: 'Teams' },
  { value: 'department', label: 'Departments' },
  { value: 'group', label: 'Groups' },
  // The backend accepts any registered event prefix (audit_logs.php) and
  // AuditLogger registers nine: auth, department, group, hr, import, member,
  // password, team, user. These two were never added to the dropdown, so HR and
  // Import events could only be filtered by hand-editing the query string.
  { value: 'hr', label: 'HR' },
  { value: 'import', label: 'Import' },
];

/** Format a date string for display */
function formatLogDate(dateStr: string): string {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} \u00B7 ${hours}:${minutes}`;
}

export const AuditLogsPage = () => {
  useAuth();
  const toast = useToast();

  const { params, setPage, setLimit, setFilter, clearFilters, correctPageIfNeeded } = usePaginationParams({
    defaultLimit: 25,
    defaultSort: 'created_at:desc',
    scrollToTop: true,
  });

  // Expanded row state (one at a time)
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Actors list for dropdown (populated from first API response)
  const [actors, setActors] = useState<AuditLogActor[]>([]);

  // Build query params from URL
  const queryParams: AuditLogsQueryParams = {
    page: params.page,
    limit: params.limit,
    sort: params.sort,
    time_range: (params.time_range as AuditLogsQueryParams['time_range']) || undefined,
    actor_id: params.actor_id ? Number(params.actor_id) : undefined,
    event_type: (params.event_type as string) || undefined,
    result: (params.result as AuditLogsQueryParams['result']) || undefined,
  };

  const { data: response, isLoading, isFetching, error } = useQuery({
    queryKey: ['audit-logs', queryParams],
    queryFn: () => auditLogsService.getPaginated(queryParams),
    placeholderData: keepPreviousData,
  });

  const logs = response?.data ?? [];
  const meta = response?.meta ?? { page: 1, limit: 25, total: 0, total_pages: 1 };

  // Update actors list from API response
  useEffect(() => {
    if (response?.actors && response.actors.length > 0) {
      setActors(response.actors);
    }
  }, [response?.actors]);

  // Correct page if needed
  useEffect(() => {
    if (response?.meta) {
      correctPageIfNeeded(response.meta.total_pages);
    }
  }, [response?.meta, correctPageIfNeeded]);

  // Check if any filters are active
  const hasActiveFilters = params.time_range || params.actor_id || params.event_type || params.result;

  // Toggle expanded row
  const toggleExpand = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // Copy params JSON to clipboard
  const handleCopyParams = useCallback(async (log: AuditLog) => {
    const text = JSON.stringify(log.params, null, 2);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  }, [toast]);

  // CSV Export
  const handleExport = useCallback(async () => {
    try {
      const exportParams: AuditLogsQueryParams = {
        ...queryParams,
        export: true,
      };
      const allLogs = await auditLogsService.getForExport(exportParams);

      const headers = ['Date', 'Time', 'Actor', 'Email', 'Event', 'Message', 'Target', 'IP', 'User Agent', 'Result', 'Params'];
      const csvRows = [headers.join(',')];

      for (const log of allLogs) {
        const d = new Date(log.created_at);
        const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
        const p = log.params || {};
        const actorName = (p.actor_name as string) || log.actor_type;
        const actorEmail = (p.actor_email as string) || '';
        const target = log.target_type ? `${log.target_type}#${log.target_id}` : '';

        csvRows.push([
          csvCell(date),
          csvCell(time),
          csvCell(actorName),
          csvCell(actorEmail),
          csvCell(log.event_key),
          csvCell(log.message),
          csvCell(target),
          csvCell(log.ip_address),
          csvCell(log.user_agent),
          csvCell(log.result),
          csvCell(JSON.stringify(log.params)),
        ].join(','));
      }

      const csvContent = csvRows.join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('href', url);
      link.setAttribute('download', `audit-logs-${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${allLogs.length} log entries`);
    } catch {
      toast.error('Failed to export audit logs');
    }
  }, [queryParams, toast]);

  if (error) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-heading mb-2">Failed to load audit logs</h3>
          <p className="text-meta">There was an error loading the audit logs.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-heading flex items-center">
            <ScrollText className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-2" />
            Audit Logging
          </h1>
          <Button onClick={handleExport} disabled={meta.total === 0}>
            <Download className="h-4 w-4 mr-1.5" />
            Export
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-surface rounded-lg shadow p-4">
          <div className="flex items-end gap-8">
            <div className="flex-1 max-w-[15%]">
              <label htmlFor="time-range" className="block text-xs font-bold text-body mb-1">
                Time Range
              </label>
              <Select
                id="time-range"
                value={(params.time_range as string) || ''}
                onChange={(e) => setFilter('time_range', e.target.value || null)}
                options={[
                  { value: '', label: 'All Time' },
                  { value: 'today', label: 'Today' },
                  { value: '7days', label: 'Last 7 Days' },
                  { value: '30days', label: 'Last 30 Days' },
                ]}
              />
            </div>
            <div className="flex-1 max-w-[18%]">
              <label htmlFor="actor-filter" className="block text-xs font-bold text-body mb-1">
                Actor
              </label>
              <Select
                id="actor-filter"
                value={params.actor_id ? String(params.actor_id) : ''}
                onChange={(e) => setFilter('actor_id', e.target.value || null)}
                options={[
                  { value: '', label: 'All Actors' },
                  ...actors.map((a) => ({ value: String(a.id), label: a.name })),
                ]}
              />
            </div>
            <div className="flex-1 max-w-[15%]">
              <label htmlFor="event-type" className="block text-xs font-bold text-body mb-1">
                Event Type
              </label>
              <Select
                id="event-type"
                value={(params.event_type as string) || ''}
                onChange={(e) => setFilter('event_type', e.target.value || null)}
                options={EVENT_TYPE_OPTIONS}
              />
            </div>
            <div className="flex-1 max-w-[12%]">
              <label htmlFor="result-filter" className="block text-xs font-bold text-body mb-1">
                Result
              </label>
              <Select
                id="result-filter"
                value={(params.result as string) || ''}
                onChange={(e) => setFilter('result', e.target.value || null)}
                options={[
                  { value: '', label: 'All' },
                  { value: 'success', label: 'Success' },
                  { value: 'failure', label: 'Failure' },
                ]}
              />
            </div>
            <div className="ml-auto">
              <Button
                variant="tertiary"
                onClick={() => clearFilters()}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Page Info */}
        {meta.total > 0 && (
          <div className="text-sm text-meta">
            Page {meta.page} of {meta.total_pages}{'\u00A0\u00A0'}&bull;{'\u00A0\u00A0'}{meta.total} entries total
          </div>
        )}

        {/* Logs List */}
        <div className="relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-surface/50 z-10 flex items-center justify-center">
              <LoadingSpinner size="md" />
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" text="Loading audit logs..." />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 bg-surface rounded-lg shadow">
              <h3 className="text-lg font-medium text-heading mb-2">
                {hasActiveFilters ? 'No audit logs match your filters' : 'No audit logs yet'}
              </h3>
              <p className="text-meta">
                {hasActiveFilters ? (
                  <button onClick={() => clearFilters()} className="text-blue-600 dark:text-blue-400 hover:underline">
                    Clear Filters
                  </button>
                ) : (
                  'Audit logs will appear as actions are performed in the system.'
                )}
              </p>
            </div>
          ) : (
            <div className="bg-surface rounded-lg shadow divide-y divide-[var(--border-default)]">
              {logs.map((log) => (
                <div key={log.id}>
                  {/* Log Row */}
                  <button
                    onClick={() => toggleExpand(log.id)}
                    className="w-full px-6 py-3 flex items-center text-left hover:bg-surface-hover transition-colors"
                  >
                    {expandedId === log.id ? (
                      <ChevronDown className="h-4 w-4 text-meta mr-3 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-meta mr-3 flex-shrink-0" />
                    )}
                    <span className="text-sm text-meta mr-3 flex-shrink-0 w-36">
                      {formatLogDate(log.created_at)}
                    </span>
                    <span className={`text-sm flex-1 ${log.result === 'failure' ? 'text-red-600 dark:text-red-400' : 'text-heading'}`}>
                      {log.message}
                    </span>
                    <span className="text-xs text-meta ml-3 flex-shrink-0 font-mono">
                      {log.event_key}
                    </span>
                  </button>

                  {/* Expanded Detail */}
                  {expandedId === log.id && (
                    <div className="px-6 pb-4 pt-1 ml-7 border-t border-[var(--border-default)]">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm mb-3">
                        <div>
                          <span className="text-meta">Actor:</span>{' '}
                          <span className="text-heading">
                            {(log.params?.actor_name as string) || log.actor_type}
                            {log.params?.actor_email ? (
                              <span className="text-meta ml-1">({String(log.params.actor_email)})</span>
                            ) : null}
                          </span>
                        </div>
                        <div>
                          <span className="text-meta">Result:</span>{' '}
                          <span className={log.result === 'failure' ? 'text-red-600 dark:text-red-400 font-medium' : 'text-green-600 dark:text-green-400'}>
                            {log.result}
                          </span>
                        </div>
                        {log.target_type && (
                          <div>
                            <span className="text-meta">Target:</span>{' '}
                            <span className="text-heading">
                              {log.target_type} #{log.target_id}
                            </span>
                          </div>
                        )}
                        {log.ip_address && (
                          <div>
                            <span className="text-meta">IP:</span>{' '}
                            <span className="text-heading font-mono text-xs">{log.ip_address}</span>
                          </div>
                        )}
                        {log.user_agent && (
                          <div className="col-span-2">
                            <span className="text-meta">User Agent:</span>{' '}
                            <span className="text-body text-xs break-all">{log.user_agent}</span>
                          </div>
                        )}
                      </div>

                      {/* Params Block */}
                      {log.params && Object.keys(log.params).length > 0 && (
                        <div className="relative">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-meta uppercase tracking-wide">Parameters</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyParams(log);
                              }}
                              className="flex items-center gap-1 text-xs text-meta hover:text-body transition-colors"
                              title="Copy to clipboard"
                            >
                              <Copy className="h-3.5 w-3.5" />
                              Copy
                            </button>
                          </div>
                          <pre className="bg-surface-app rounded-md p-3 text-xs text-body font-mono overflow-x-auto max-h-48">
                            {JSON.stringify(log.params, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {meta.total > 0 && (
          <Pagination
            page={meta.page}
            limit={meta.limit}
            total={meta.total}
            totalPages={meta.total_pages}
            entityName="entries"
            onPageChange={setPage}
            onLimitChange={setLimit}
            isLoading={isFetching}
          />
        )}
      </div>
    </Layout>
  );
};
